<template>
    <form class="settings-form">
        <div class="tabs">
            <span
                v-for="tab in TABS"
                :key="tab"
                class="tab"
                :class="{ selected: activeTab === tab }"
                @click="activeTab = tab"
            >
                {{ $translate(tab) }}
            </span>
        </div>

        <div v-show="activeTab === 'general'" class="general-tab">
            <div class="general-settings mb-3">
                <form ref="settingsForm" class="basic-settings" @submit.stop.prevent="save">
                    <b-form-group
                        class="first-name"
                        :label="$translate('display_name')"
                        :invalid-feedback="$translate('first_name_cant_be_empty')"
                    >
                        <b-form-input v-model="userModel.firstName" type="text" required />
                    </b-form-group>

                    <b-form-group
                        class="last-name"
                        :label="$translate('last_name')"
                        :invalid-feedback="$translate('last_name_cant_be_empty')"
                    >
                        <b-form-input v-model="userModel.lastName" type="text" required />
                    </b-form-group>

                    <b-form-group class="email" :label="$translate('contact_email')">
                        <b-form-input v-model="userModel.contactEmail" type="email" />
                    </b-form-group>

                    <b-form-group
                        class="clinic-name"
                        :label="$translate('clinic_name')"
                        :invalid-feedback="$translate('clinic_name_cant_be_empty')"
                    >
                        <b-form-input v-model="tenantModel.clinicName" type="text" />
                    </b-form-group>

                    <b-form-group
                        class="city"
                        :label="$translate('city')"
                        :invalid-feedback="$translate('city_name_cant_be_empty')"
                    >
                        <b-form-input v-model="tenantModel.cityName" type="text" />
                    </b-form-group>

                    <b-form-group class="language" :label="$translate('language_preference')">
                        <b-form-select
                            id="langSelect"
                            v-model="tenantModel.language"
                            :options="languageOptions"
                        />
                    </b-form-group>

                    <b-form-group
                        id="colorSelect"
                        class="color"
                        :label="$translate('color_preference')"
                    >
                        <b-dropdown variant="transparent" menu-class="w-100" no-caret>
                            <template v-slot:button-content>
                                <span class="color-selection">
                                    <span class="color-selected" :class="tenantModel.color"> </span>
                                    <img :src="`${WELL_STATUS_ICONS_PATH}/DEFAULT.svg`" alt="" />
                                </span>
                            </template>

                            <div
                                v-for="(color, key) in colorOptions"
                                :key="key"
                                @click="tenantModel.color = color"
                            >
                                <div
                                    class="color-item"
                                    :class="[color, { 'is-selected': isColorSelected(color) }]"
                                ></div>
                            </div>
                        </b-dropdown>
                    </b-form-group>

                    <b-form-group class="ai-toggle">
                        <b-checkbox
                            v-model="tenantModel.settings.isAiActiveTemp"
                            switch
                            size="lg"
                            :disabled="showIsAiActiveConfirmDialog"
                            @change="showIsAiActiveConfirmDialog = true"
                        >
                            CHLOE EQ Insights
                        </b-checkbox>

                        <div v-if="showIsAiActiveConfirmDialog" class="confirmation-dialog">
                            <template v-if="!tenantModel.settings.isAiActive">
                                <p>
                                    <b>CAUTION:</b> Investigational device. Limited by Federal (or
                                    United States) law to investigational use.
                                </p>
                                <p>
                                    <b>CAUTION:</b> CHLOE EQ shall not be used in clinical
                                    applications. To be used for simulated-use testing of user
                                    interaction only.
                                </p>
                            </template>

                            <p v-if="tenantModel.settings.isAiActive">
                                <b>NOTE:</b> You are about to turn off CHLOE EQ Insights.
                            </p>

                            <p class="question"><b>Do you wish to proceed?</b></p>

                            <div class="confirm-actions">
                                <b-button
                                    class="btn btn-primary confirm"
                                    @click="confirmIsAiActiveTempValue"
                                >
                                    {{ tenantModel.settings.isAiActive ? 'Yes' : 'I confirm' }}
                                </b-button>
                                <b-button
                                    class="btn btn-secondary"
                                    @click="revertIsAiActiveTempValue"
                                >
                                    {{ tenantModel.settings.isAiActive ? 'No' : 'Cancel' }}
                                </b-button>
                            </div>
                        </div>
                    </b-form-group>

                    <b-form-group class="manual-approval">
                        <b-checkbox
                            v-model="tenantModel.settings.isFertilizationManualApprovalActiveTemp"
                            switch
                            size="lg"
                            :disabled="showIsFertilizationManualApprovalActiveConfirmDialog"
                            @change="showIsFertilizationManualApprovalActiveConfirmDialog = true"
                        >
                            Manual fertilisation check approval
                        </b-checkbox>

                        <div
                            v-if="showIsFertilizationManualApprovalActiveConfirmDialog"
                            class="confirmation-dialog"
                        >
                            <p>
                                <b>NOTE:</b>
                                You are about to
                                {{
                                    tenantModel.settings.isFertilizationManualApprovalActiveTemp
                                        ? 'activate'
                                        : 'deactivate'
                                }}
                                Manual fertilisation approval feature
                            </p>

                            <p class="question"><b>Do you wish to proceed?</b></p>

                            <div class="confirm-actions">
                                <b-button
                                    class="btn btn-primary confirm"
                                    @click="confirmIsFertilizationManualApprovalActiveTempValue"
                                >
                                    {{ $translate('yes') }}
                                </b-button>
                                <b-button
                                    class="btn btn-secondary"
                                    @click="revertIsFertilizationManualApprovalActiveTempValue"
                                >
                                    {{ $translate('cancel') }}
                                </b-button>
                            </div>
                        </div>
                    </b-form-group>
                </form>

                <div class="mr-3">
                    <span class="justify-center text-bold text_primary">Profile Image</span><br />

                    <croppa
                        ref="avatarCroppa"
                        class="mt-1"
                        :width="130"
                        :height="130"
                        :initial-image="avatarUrl"
                        :placeholder="$translate('select_image')"
                        :placeholder-font-size="12"
                        :prevent-white-space="true"
                        :placeholder-color="'default'"
                        :accept="'image/*'"
                        :file-size-limit="0"
                        :quality="1"
                        :zoom-speed="30"
                        :show-remove-button="true"
                        :remove-button-color="'red'"
                        :remove-button-size="20"
                        @click="avatarChanged = true"
                    />
                </div>
            </div>

            <div class="cycle-details-checkbox">
                <b-form-checkbox
                    v-model="tenantModel.settings.isShowFertStatus"
                    :value="true"
                    :unchecked-value="false"
                />
                <label class="cycle-label">Cycle Details</label>

                <div v-if="tenantModel.settings.isShowFertStatus" class="cycle-details">
                    <b-dropdown class="selected-cycle-method">
                        <template v-slot:button-content>
                            {{ $translate(tenantModel.settings.selectedCycleMethod) }}
                        </template>

                        <b-dropdown-item
                            v-for="option in availableCycleMethods"
                            :key="option"
                            @click="selectDefaultFertMethod(option)"
                        >
                            {{ $translate(option) }}
                        </b-dropdown-item>
                    </b-dropdown>

                    <div class="cycle-info">
                        <div class="insemination-title">Set default insemination method.</div>
                        <div class="insemination-body">
                            *Method can be updated for individual slides and embryos at any time in
                            the treatment view.
                        </div>
                    </div>
                </div>
            </div>

            <div class="mb-2">Clinic Logo</div>

            <div class="h-divider" />

            <div class="logo-container">
                <div class="file-upload-container">
                    <label for="clinicLogo" class="upload-file-button">Upload File </label>
                    <input
                        id="clinicLogo"
                        ref="clinicLogo"
                        class="upload-file-input"
                        type="file"
                        name="clinicLogo"
                        accept="image/*"
                    />
                    <img
                        id="clinicLogoViewer"
                        ref="clinicLogoImg"
                        :src="tenantModel.clinicLogoUrl"
                        class="uploaded-logo"
                        width="250px"
                        alt=""
                    />
                </div>
                <div class="logo-message">
                    We recommend to upload a high resolution png logo file (less then 2MB)
                </div>
            </div>

            <span class="">{{ $translate('security') }}</span>

            <div class="h-divider" />

            <div class="pb-2 mb-3">
                <div class="mt-4 mb-1 justify-between">
                    <b-form-group :label="$translate('login_email')">
                        <span class="">&nbsp;&nbsp;&nbsp;&nbsp;{{ user.email }}</span>
                    </b-form-group>

                    <div class="pt-4">
                        <b-button class="btn btn-secondary" @click="changePassword">
                            {{ $translate('change_password') }}
                        </b-button>
                    </div>
                </div>
            </div>

            <span class="">{{ $translate('data_integration') }}</span>

            <div class="h-divider" />

            <div>
                <div class="mt-4" v-html="$translate('agent_integration_explanation')"></div>

                <div class="mb-1 mt-4 display-flex">
                    <b-form-group
                        class="mr-3 agent-password-input"
                        :label="$translate('agent_password')"
                        :invalid-feedback="$translate('agent_password_cant_be_empty')"
                    >
                        <b-form-input
                            id="connector-password-input"
                            v-model="agentPassword"
                            :type="revealAgentPassword ? 'text' : 'password'"
                            class="connector-password-input"
                            :disabled="agentDownloadUrl"
                            required
                        />

                        <i
                            id="togglePassword"
                            class="fa fa-eye p-1"
                            @click="revealAgentPassword = !revealAgentPassword"
                        ></i>
                    </b-form-group>

                    <b-form-group :label="$translate('clinic')">
                        <b-dropdown
                            v-if="user._id"
                            variant="dark"
                            class="mr-5"
                            :disabled="!!agentDownloadUrl"
                            :text="tenant && $translate(tenant.name)"
                        >
                            <b-dropdown-item
                                v-for="tenant in user.tenants"
                                :key="tenant._id"
                                :class="`${tenant.name}-tenant-dropdown-option`"
                                href="#"
                                @click="changeTenant(tenant)"
                            >
                                {{ $translate(tenant.name) }}
                            </b-dropdown-item>
                        </b-dropdown>

                        <b-button
                            v-if="!agentDownloadUrl"
                            class="btn btn-secondary"
                            :disabled="!downloadAgentButtonEnabled"
                            @click="downloadConnector"
                        >
                            {{ $translate('get_download_link') }}
                        </b-button>
                    </b-form-group>
                </div>

                <div v-if="agentDownloadUrl" class="px-3 pb-2">
                    <div class="mt-4 mb-1">
                        <div class="col-sm-12 my-3">
                            {{ $translate('connector_username') }}:<br /><b>{{ agentUserName }}</b>
                        </div>
                        <div class="col-sm-12 my-3">
                            <a :href="agentDownloadUrl">
                                {{ $translate('download') }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-show="activeTab === 'share_with_patient'" class="share-to-patient-tab">
            <p>Custom day descriptions</p>

            <div
                v-for="(day, count) in tenantModel.settings.shareToPatientDayDescriptions"
                :key="count"
                class="day-block"
            >
                Day {{ count }}

                <ul class="lang-inputs-list">
                    <li v-for="(customText, code) in day" :key="code">
                        <b-form-group :label="LANGUAGES[code]">
                            <b-textarea
                                v-model="
                                    tenantModel.settings.shareToPatientDayDescriptions[count][code]
                                "
                            />
                        </b-form-group>
                    </li>
                </ul>
            </div>
        </div>
    </form>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import $ from 'jquery'
import 'password-strength-meter'
import 'vue-croppa/dist/vue-croppa.css'
import Utils from '../../../business/utils/Utils'
import UIUtils from '../../../business/utils/UIUtils'
import BackendProvider from '../../../business/services/BackendProvider'
import { CLINIC_LOGO_MAX_UPLOAD_SIZE, DEFAULT_AVATAR } from '../../../business/const'

const WELL_STATUS_ICONS_PATH = '/static/svg/well_status'
const TABS = ['general', 'share_with_patient']

const DEFAULT_DAY_DESCRIPTIONS_KEYS = {
    1: 'day_1_description',
    2: 'day_2_description',
    3: 'day_3_description',
    4: 'day_4_description',
    5: 'day_5_description'
}

const LANGUAGES = {
    en: 'English',
    el: 'Greek',
    pt: 'Portuguese',
    es: 'Spanish',
    tr: 'Turkish'
}

export default {
    name: 'Settings',
    data: () => ({
        TABS,
        LANGUAGES,
        activeTab: 'general',
        userModel: {
            firstName: '',
            lastName: '',
            contactEmail: '',
            selectedTenant: null
        },
        tenantModel: {
            clinicName: '',
            cityName: '',
            clinicLogoUrl: '',
            language: '',
            color: '',
            settings: {
                isAiActive: false,
                isFertilizationManualApprovalActive: false,
                isAiActiveTemp: false,
                isFertilizationManualApprovalActiveTemp: false,
                isShowFertStatus: false,
                selectedCycleMethod: 'ICSI',
                shareToPatientDayDescriptions: {
                    1: { en: '', el: '', pt: '', es: '', tr: '' },
                    2: { en: '', el: '', pt: '', es: '', tr: '' },
                    3: { en: '', el: '', pt: '', es: '', tr: '' },
                    4: { en: '', el: '', pt: '', es: '', tr: '' },
                    5: { en: '', el: '', pt: '', es: '', tr: '' }
                }
            }
        },
        avatarChanged: false,
        cycleOptions: ['ICSI', 'IVF', 'No default'],
        colorOptions: ['orange', 'blue', 'red', 'turquoise', 'pink', 'black'],
        downloadAgentButtonEnabled: false,
        revealAgentPassword: false,
        agentUserName: null,
        agentDownloadUrl: null,
        agentPassword: '',
        reloadRequired: false,
        showIsAiActiveConfirmDialog: false,
        showIsFertilizationManualApprovalActiveConfirmDialog: false,
        WELL_STATUS_ICONS_PATH
    }),
    computed: {
        ...mapState({
            user: state => state.common.user,
            locale: state => state.locale.locale,
            locales: state => state.locale.locales
        }),
        ...mapGetters({
            tenant: 'common/getTenant',
            translationByLang: 'locale/translationByLang'
        }),
        avatarUrl() {
            const { imageUrl } = this.user
            const noImage = !imageUrl || imageUrl === 'empty' || imageUrl.includes('licdn.com')

            return noImage ? DEFAULT_AVATAR : imageUrl
        },
        languageOptions() {
            return Object.values(this.locales).map(locale => ({
                value: locale.code,
                text: locale.name
            }))
        },
        availableCycleMethods() {
            return this.cycleOptions.filter(
                option => this.tenantModel.settings.selectedCycleMethod !== option
            )
        }
    },
    watch: {
        showIsAiActiveConfirmDialog() {
            this.$emit(
                'setDisableOKButton',
                this.showIsFertilizationManualApprovalActiveConfirmDialog ||
                    this.showIsAiActiveConfirmDialog
            )
        },
        showIsFertilizationManualApprovalActiveConfirmDialog() {
            this.$emit(
                'setDisableOKButton',
                this.showIsFertilizationManualApprovalActiveConfirmDialog ||
                    this.showIsAiActiveConfirmDialog
            )
        }
    },
    mounted() {
        this.setDefaults()

        this.$nextTick(() => {
            this.setDownloadAgentButton()

            const selectEl = document.querySelector(`#langSelect`)
            selectEl.value = (this.tenant && this.tenant.language) || this.locale.code
            selectEl.dispatchEvent(new Event('change'))

            this.setLogoInput()
        })
    },
    methods: {
        setDefaults() {
            this.userModel.firstName = this.user.firstName
            this.userModel.lastName = this.user.lastName
            this.userModel.selectedTenant = this.user.tenants[0]
            this.userModel.contactEmail = this.user.contactEmail || this.user.email

            this.tenantModel.clinicName = this.tenant.clinicName
            this.tenantModel.clinicLogoUrl = this.tenant.logoUrl
            this.tenantModel.cityName = this.tenant.city
            this.tenantModel.color = (this.tenant && this.tenant.color) || 'black'

            const {
                inseminationType,
                showCycleDetails,
                isAiActive,
                isFertilizationManualApprovalActive
            } = this.tenant.settings

            if (inseminationType) {
                this.tenantModel.settings.selectedCycleMethod = inseminationType
            }
            this.tenantModel.settings.isShowFertStatus = !!showCycleDetails
            this.tenantModel.settings.isFertilizationManualApprovalActive =
                this.tenantModel.settings.isFertilizationManualApprovalActiveTemp =
                    !!isFertilizationManualApprovalActive
            this.tenantModel.settings.isAiActive = this.tenantModel.settings.isAiActiveTemp =
                isAiActive

            this.setDayDescriptions()
        },
        setLogoInput() {
            const logoInput = this.$refs.clinicLogo
            const clinicLogoViewer = this.$refs.clinicLogoImg

            if (logoInput && clinicLogoViewer) {
                logoInput.addEventListener('change', e => {
                    const [file] = e.target.files
                    if (file) {
                        if (file.size > CLINIC_LOGO_MAX_UPLOAD_SIZE) {
                            UIUtils.toast('File size must be less than 2MB', 'error')
                            e.target.value = null
                            return
                        }
                        clinicLogoViewer.src = URL.createObjectURL(file)
                    }
                })
            }
        },
        setDayDescriptions() {
            const model = this.tenantModel.settings.shareToPatientDayDescriptions
            const descriptions = this.tenant.settings.shareToPatientDayDescriptions

            Object.keys(model).forEach(dayCount => {
                const dayModel = model[dayCount]

                Object.keys(dayModel).forEach(langKey => {
                    if (descriptions && descriptions[dayCount] && descriptions[dayCount][langKey]) {
                        dayModel[langKey] = descriptions[dayCount][langKey]
                    } else {
                        dayModel[langKey] = this.translationByLang(
                            langKey,
                            DEFAULT_DAY_DESCRIPTIONS_KEYS[dayCount]
                        )
                    }
                })
            })
        },
        setDownloadAgentButton() {
            const self = this

            this.$nextTick(() => {
                const jQueryInput = $('#connector-password-input')

                jQueryInput.password({ minimumLength: 6 })
                jQueryInput.on('password.score', (e, score) => {
                    if (score > 70) {
                        self.downloadAgentButtonEnabled = true
                    }
                })
            })
        },
        changeTenant(tenant) {
            this.selectedTenant = tenant
        },
        async save() {
            if (this.$refs.settingsForm && this.$refs.settingsForm.checkValidity()) {
                await this.sendRequest()
            }
        },
        async sendRequest() {
            try {
                UIUtils.showLoadingMask()

                this.reloadRequired = false

                await this.updateClinicLogo()
                await this.saveUserProfile()
                await this.saveTenantSettings()

                if (this.reloadRequired) {
                    await this.$store.dispatch('common/setUserData')
                    this.$store.commit('common/setSelectedTenant', this.user.tenants[0])
                }
                this.$emit('ok')
            } catch (e) {
                console.error(e)

                if (e.body && e.body.message) {
                    UIUtils.toast(e.body.message.toString(), 'error')
                } else if (e.body && e.body.error) {
                    UIUtils.toast(e.body.error.toString(), 'error')
                } else if (e.message) {
                    UIUtils.toast(e.message.toString(), 'error')
                } else {
                    UIUtils.toast(e.statusText.toString(), 'error')
                }
                return false
            } finally {
                UIUtils.hideLoadingMask()
            }
        },
        async saveUserProfile() {
            this.$log.actionEvent('change_user_profile_settings')

            if (this.avatarChanged) {
                await this.updateUserAvatar()
            }

            const { firstName, lastName, contactEmail } = this.userModel

            if (
                firstName !== this.user.firstName ||
                lastName !== this.user.lastName ||
                contactEmail !== (this.user.contactEmail || this.user.email)
            ) {
                await BackendProvider.updateUserProfile({
                    firstName: firstName,
                    lastName: lastName,
                    contactEmail: contactEmail
                })
                this.reloadRequired = true
            }
        },
        async saveTenantSettings() {
            const { clinicName, cityName, language, color, settings } = this.tenantModel
            const {
                isAiActive,
                isShowFertStatus,
                selectedCycleMethod,
                isFertilizationManualApprovalActive
            } = settings

            const body = {
                settings: {}
            }
            // tenant properties
            if (cityName !== this.tenant.city) {
                body.city = cityName
            }
            if (clinicName !== this.tenant.clinicName) {
                body.clinicName = clinicName
            }
            if (language !== this.tenant.language) {
                body.language = language
            }
            if (color !== this.tenant.color) {
                body.color = color
            }

            if (isAiActive !== this.tenant.settings.isAiActive) {
                body.settings.isAiActive = isAiActive
            }
            if (
                isFertilizationManualApprovalActive !==
                this.tenant.settings.isFertilizationManualApprovalActive
            ) {
                body.settings.isFertilizationManualApprovalActive =
                    isFertilizationManualApprovalActive
            }
            if (
                isShowFertStatus &&
                selectedCycleMethod &&
                selectedCycleMethod.toUpperCase() !== this.tenant.settings.inseminationType
            ) {
                body.settings.inseminationType = selectedCycleMethod
                    .toUpperCase()
                    .replace(/ /g, '_')
            }
            if (isShowFertStatus !== this.tenant.settings.showCycleDetails) {
                body.settings.showCycleDetails = isShowFertStatus
            }

            if (
                JSON.stringify(this.tenantModel.settings.shareToPatientDayDescriptions) !==
                JSON.stringify(this.tenant.settings.shareToPatientDayDescriptions)
            ) {
                body.settings.shareToPatientDayDescriptions =
                    this.tenantModel.settings.shareToPatientDayDescriptions
            }

            if (Object.keys(body.settings).length === 0) {
                delete body.settings
            }

            if (Object.keys(body).length) {
                await BackendProvider.updateTenantSettings(body, this.tenant._id)
                this.reloadRequired = true
            }
        },
        async updateUserAvatar() {
            const blob = await this.$refs.avatarCroppa.promisedBlob()

            this.$store.state.common.user.imageUrl = await BackendProvider.updateAvatar({
                imageBase64: await Utils.getBase64StringFromFile(blob)
            })
        },
        async updateClinicLogo() {
            const logoInput = this.$refs.clinicLogo

            if (logoInput.files.length > 0) {
                const [file] = logoInput.files

                if (file.size > CLINIC_LOGO_MAX_UPLOAD_SIZE) {
                    UIUtils.toast('File size must be less than 2MB', 'error')
                } else {
                    const body = {
                        imageBuffer: await Utils.getBase64StringFromFile(file)
                    }
                    await BackendProvider.updateClinicLogo(body, this.tenant._id)

                    this.reloadRequired = true
                }
            }
        },
        isColorSelected(color) {
            return color === this.tenantModel.color
        },
        confirmIsAiActiveTempValue() {
            this.tenantModel.settings.isAiActive = this.tenantModel.settings.isAiActiveTemp
            this.showIsAiActiveConfirmDialog = false
        },
        revertIsAiActiveTempValue() {
            this.tenantModel.settings.isAiActiveTemp = this.tenantModel.settings.isAiActive
            this.showIsAiActiveConfirmDialog = false
        },
        confirmIsFertilizationManualApprovalActiveTempValue() {
            this.tenantModel.settings.isFertilizationManualApprovalActive =
                this.tenantModel.settings.isFertilizationManualApprovalActiveTemp
            this.showIsFertilizationManualApprovalActiveConfirmDialog = false
        },
        revertIsFertilizationManualApprovalActiveTempValue() {
            this.tenantModel.settings.isFertilizationManualApprovalActiveTemp =
                this.tenantModel.settings.isFertilizationManualApprovalActive
            this.showIsFertilizationManualApprovalActiveConfirmDialog = false
        },
        selectDefaultFertMethod(cycleMethod) {
            this.tenantModel.settings.selectedCycleMethod = cycleMethod
        },
        changePassword() {
            UIUtils.toast(this.$translate('password_reset_instructions'))
        },
        async downloadConnector() {
            if (this.selectedTenant) {
                UIUtils.showLoadingMask()

                const { email, agentDownloadUrl } = await BackendProvider.generateAgentDownloadUrl(
                    this.selectedTenant._id,
                    this.agentPassword
                )

                this.agentUserName = email
                this.agentDownloadUrl = agentDownloadUrl

                UIUtils.hideLoadingMask()
            }
        }
    }
}
</script>

<style lang="scss">
.settings-form {
    .tabs {
        width: 100%;
        margin-bottom: 1.5rem;
        padding-bottom: 0.5rem;
        border-bottom: $border-narrow-dark;

        .tab {
            &:not(:last-child) {
                margin-right: 1rem;
            }

            &:hover,
            &.selected {
                color: $primary;
            }

            &:not(.selected) {
                cursor: pointer;
            }
        }
    }

    .general-tab,
    .share-to-patient-tab {
        width: 742px;
    }

    .general-tab {
        .general-settings {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            column-gap: 20px;
            justify-items: end;
            justify-content: space-between;

            .basic-settings {
                width: 100%;
                display: grid;
                column-gap: 20px;
                align-items: end;
                grid-template-columns: repeat(2, 1fr);
                grid-template-areas:
                    'firstname   lastname'
                    'email      email'
                    'clinicname city'
                    'language   color'
                    'aitoggle   aitoggle'
                    'manualapproval   manualapproval';

                .first-name {
                    grid-area: firstname;
                }
                .last-name {
                    grid-area: lastname;
                }
                .email {
                    grid-area: email;
                }
                .clinic-name {
                    grid-area: clinicname;
                }
                .city {
                    grid-area: city;
                }
                .language {
                    grid-area: language;

                    #langSelect {
                        background: none;
                        color: $white;
                        border-color: $light;
                        cursor: pointer;

                        option {
                            background-color: $dark;
                        }
                    }
                }

                .color {
                    grid-area: color;

                    img {
                        margin-top: 0.2rem;
                        height: 1rem;
                    }
                    .color-selection {
                        display: flex;
                        justify-content: space-around;
                    }
                    #colorSelect {
                        background: none;
                        color: $white;
                        border-color: $light;
                        cursor: pointer;

                        option {
                            background-color: $dark;
                        }
                    }
                    .btn {
                        min-width: 12rem;
                        padding: 5px 0px 5px 10px;

                        @media screen and #{$new-break2close} {
                            min-width: 6rem;
                        }
                        @media screen and #{$new-break3} {
                            min-width: 15.5rem;
                        }
                        @media screen and #{$new-break4} {
                            min-width: 12rem;
                        }
                    }
                    .dropdown-menu {
                        background-color: $white;
                    }
                    .dropdown-menu.show {
                        display: grid;
                        grid-template-columns: auto auto auto;
                        padding: 1.2rem;
                        grid-gap: 2.1rem;
                    }
                    .color-item {
                        height: 1.5rem;
                        width: 1.6rem;
                        border: 1px solid transparent;
                        cursor: pointer;
                        text-align: center;
                        border-radius: 5rem;
                        background-color: $report-color;

                        &.is-selected {
                            -webkit-box-shadow: 0 0 10px 2px $report-color;
                            -moz-box-shadow: 0 0 10px 2px $report-color;
                            box-shadow: 0 0 10px 2px $report-color;
                        }
                    }
                    .color-selected {
                        border-radius: 10rem;
                        height: 1.4rem;
                        min-width: 8rem;
                        color: $report-color;
                        background-color: $report-color;

                        @media screen and #{$new-break2close} {
                            min-width: 3rem;
                        }
                        @media screen and #{$new-break3} {
                            min-width: 11rem;
                        }
                        @media screen and #{$new-break4} {
                            min-width: 8rem;
                        }
                    }
                }

                .ai-toggle,
                .manual-approval {
                    grid-area: aitoggle;

                    .pill-switch {
                        flex-direction: row-reverse;
                        justify-content: flex-end;
                    }

                    .confirmation-dialog {
                        border: solid 1px $light;
                        padding: 1rem;
                        margin: 0.5rem 0;

                        .question {
                            text-align: center;
                        }

                        .confirm-actions {
                            padding: 0 2rem;
                            display: flex;
                            justify-content: space-between;

                            .confirm {
                                background-color: $dark-blue !important;
                                color: $light-3 !important;
                            }
                        }
                    }
                }
                .manual-approval {
                    grid-area: manualapproval;
                }
            }
        }

        .logo-container {
            padding: 1rem;

            .file-upload-container {
                display: flex;
                justify-content: center;
                align-items: center;
                position: relative;
                height: 15rem;
                width: 25rem;
                border: 1px dashed $dark;

                .uploaded-logo {
                    max-width: 100%;
                    max-height: 100%;
                    padding: 1rem;
                }

                .upload-file-button {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    z-index: 9;
                    cursor: pointer;
                    padding: 0.5rem 0.8rem;
                    border-radius: 50px;
                    background-color: $blue;
                }

                .upload-file-input {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    opacity: 0;
                }
            }
            .logo-message {
                width: 25rem;
                text-align: center;
                padding: 1rem;
            }
        }

        .cycle-details-checkbox {
            display: flex;

            .cycle-label {
                margin-right: 6rem;
            }
            #cycleSelect {
                background: none;
                color: $white;
                border-color: $light;
                cursor: pointer;

                option {
                    background-color: $dark;
                }
            }
            .cycle-details {
                display: inline-flex;
                width: 28rem;

                .selected-cycle-method {
                    height: 2.1rem;
                    width: 5rem;
                    margin-right: 3rem;
                }
                .cycle-info {
                    .insemination-title {
                        font-size: 1rem;
                    }
                    .insemination-body {
                        font-size: 0.75rem;
                        color: $light;
                    }
                }
                .dropdown-menu {
                    min-width: 0;
                }
            }
        }
    }

    .share-to-patient-tab {
        .day-block {
            display: grid;
            grid-template-columns: 50px auto;
            gap: 20px;

            &:not(:first-child) {
                margin-top: 1rem;
            }

            .lang-inputs-list {
                list-style-type: none;
            }
        }
    }
}
</style>
